import ApexCharts, { ApexOptions } from "apexcharts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStickyYAxis } from "../../../../hooks/useStickyYAxis";
import { getCSS, getCSSVariableValue } from "../../../assets/ts/_utils";
import { useThemeMode } from "../../layout/theme-mode/ThemeModeProvider";

type Props = {
  className: string;
  label: string[];
  dataList: number[];
  title: string;
  eyebrow?: string;
  description?: string;
};

type FilterLimit = 10 | 20 | 50 | "all";

const ChartsWidget2: React.FC<Props> = ({
  className,
  label,
  dataList,
  title,
  eyebrow,
  description,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<ApexCharts | null>(null);
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const prevScrollableRef = useRef<boolean>(false);
  const { mode } = useThemeMode();
  const [filterLimit, setFilterLimit] = useState<FilterLimit>(10);

  // Group and sort locations by event count descending
  const processedData = useMemo(() => {
    const map = new Map<string, number>();
    for (let i = 0; i < label.length; i++) {
      const rawName = label[i];
      const trimmedName = typeof rawName === "string" ? rawName.trim() : "";
      const val = Number(dataList[i]) || 0;
      if (trimmedName && val > 0) {
        map.set(trimmedName, (map.get(trimmedName) || 0) + val);
      }
    }
    const list: { label: string; value: number }[] = [];
    map.forEach((value, lbl) => {
      list.push({ label: lbl, value });
    });
    return list.sort((a, b) => b.value - a.value);
  }, [label, dataList]);

  const hasData = processedData.length > 0;

  // Compute view limit options based on total items
  const filterOptions = useMemo(() => {
    const total = processedData.length;
    if (total <= 10) {
      return [];
    }
    const options: { label: string; value: FilterLimit }[] = [
      { label: "Top 10", value: 10 },
    ];
    if (total > 10 && total <= 20) {
      options.push({ label: `All (${total})`, value: "all" });
    } else if (total > 20 && total <= 50) {
      options.push({ label: "Top 20", value: 20 });
      options.push({ label: `All (${total})`, value: "all" });
    } else if (total > 50) {
      options.push({ label: "Top 20", value: 20 });
      options.push({ label: "Top 50", value: 50 });
      options.push({ label: `All (${total})`, value: "all" });
    }
    return options;
  }, [processedData.length]);

  const visibleItems = useMemo(() => {
    if (filterLimit === "all" || processedData.length <= 10) {
      return processedData;
    }
    return processedData.slice(0, filterLimit);
  }, [processedData, filterLimit]);

  const isScrollable = visibleItems.length > 12;

  useEffect(() => {
    if (!chartRef.current || !visibleItems.length || !hasData) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return;
    }

    const height = parseInt(getCSS(chartRef.current, "height")) || 350;
    const categories = visibleItems.map((item) => item.label);
    const seriesData = visibleItems.map((item) => item.value);
    const options = getChartOptions(height, categories, seriesData, mode);

    // Rebuild chart only on first render or when scrollability changes
    const scrollabilityChanged = prevScrollableRef.current !== isScrollable;
    prevScrollableRef.current = isScrollable;

    if (!chartInstanceRef.current || scrollabilityChanged) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      const chart = new ApexCharts(chartRef.current, options);
      chart.render().then(() => {
        if (chartRef.current) {
          const textElements =
            chartRef.current.querySelectorAll<SVGTextElement>(
              ".apexcharts-xaxis-texts-g text, .apexcharts-xaxis-label",
            );
          textElements.forEach((el, index) => {
            const fullName = categories[index];
            if (fullName) {
              el.setAttribute("title", fullName);
              el.style.cursor = "pointer";
              if (!el.querySelector("title")) {
                const svgTitle = document.createElementNS(
                  "http://www.w3.org/2000/svg",
                  "title",
                );
                svgTitle.textContent = fullName;
                el.appendChild(svgTitle);
              }
            }
          });
        }
      });
      chartInstanceRef.current = chart;
    } else {
      chartInstanceRef.current.updateOptions(options, true, true);
    }
  }, [chartRef, mode, visibleItems, hasData, isScrollable]);

  // Clean up chart instance on unmount
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  useStickyYAxis(scrollWrapperRef, chartRef, isScrollable);

  return (
    <div className={`card dashboard-chart-card ${className}`}>
      <div className="card-header border-0 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
        <div className="dashboard-chart-card__heading">
          {eyebrow ? (
            <span className="dashboard-chart-card__eyebrow">{eyebrow}</span>
          ) : null}
          <h3 className="dashboard-chart-card__title">{title}</h3>
          <p className="dashboard-chart-card__description">
            {description ?? "A detailed breakdown of group distribution."}
          </p>
        </div>
        {filterOptions.length > 0 && (
          <div className="d-flex align-items-center gap-2 flex-wrap ms-md-auto">
            <span className="text-muted fw-bold fs-8 d-none d-sm-inline">
              View:
            </span>
            <div className="dashboard-chart-filter-group">
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`dashboard-chart-filter-btn ${
                    filterLimit === opt.value ? "active" : ""
                  }`}
                  onClick={() => setFilterLimit(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="card-body dashboard-chart-card__body">
        {!hasData ? (
          <div className="dashboard-chart-empty">
            <span className="dashboard-chart-empty__icon">EL</span>
            <span className="dashboard-chart-empty__copy">
              <span className="dashboard-chart-empty__title">
                No location activity yet
              </span>
              <span className="dashboard-chart-empty__subtitle">
                Location demand will show up here once group traffic starts
                building.
              </span>
            </span>
          </div>
        ) : null}
        {isScrollable && (
          <div className="d-flex justify-content-end align-items-center mb-2 pe-1">
            <span className="text-muted fs-8 fw-semibold">
              ↔ Scroll horizontally to explore all {visibleItems.length}{" "}
              locations
            </span>
          </div>
        )}
        <div ref={scrollWrapperRef} className="dashboard-chart-scroll-wrapper">
          <div
            ref={chartRef}
            className="dashboard-chart-canvas"
            style={{
              height: "350px",
              minWidth: isScrollable
                ? `${visibleItems.length * 56}px`
                : undefined,
              width: isScrollable ? `${visibleItems.length * 56}px` : "100%",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export { ChartsWidget2 };

function getChartOptions(
  height: number,
  categories: string[],
  dataList: number[],
  mode: string,
): ApexOptions {
  const labelColor = getCSSVariableValue("--bs-gray-600") || "#708198";
  const borderColor = "#E2E8F0";
  const valueColor = mode === "dark" ? "#F8FAFC" : "#0F172A";

  return {
    series: [
      {
        name: "Groups",
        data: dataList,
      },
    ],
    chart: {
      fontFamily: "inherit",
      type: "bar",
      height: height,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
        animateGradually: {
          enabled: true,
          delay: 40,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth:
          categories.length <= 4
            ? "24%"
            : categories.length <= 10
            ? "40%"
            : "48%",
        borderRadius: 8,
        dataLabels: {
          position: "top",
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val > 0 ? val.toString() : "";
      },
      offsetY: -10,
      style: {
        fontSize: "11px",
        fontWeight: 700,
        colors: [valueColor],
      },
      background: {
        enabled: false,
        dropShadow: {
          enabled: false,
        },
      },
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      show: true,
      width: 0,
      colors: ["transparent"],
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        rotate: -45,
        rotateAlways: categories.length > 5,
        hideOverlappingLabels: false,
        trim: true,
        maxHeight: 110,
        style: {
          colors: labelColor,
          fontSize: "11px",
          fontWeight: 600,
        },
        formatter: function (val: string) {
          if (typeof val === "string" && val.length > 20) {
            return val.substring(0, 18) + "...";
          }
          return val;
        },
      },
      tooltip: {
        enabled: true,
        formatter: function (_val: string, opts?: any) {
          const idx = opts?.dataPointIndex;
          if (idx !== undefined && categories[idx]) {
            return categories[idx];
          }
          return _val;
        },
      },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: function (val: number) {
          return Math.round(val).toString();
        },
        style: {
          colors: "#94A3B8",
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.18,
        gradientToColors: ["#38BDF8"],
        inverseColors: false,
        opacityFrom: 0.97,
        opacityTo: 0.82,
        stops: [0, 100],
      },
    },
    states: {
      normal: {
        filter: {
          type: "none",
          value: 0,
        },
      },
      hover: {
        filter: {
          type: "lighten",
          value: 0.04,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: "lighten",
          value: 0.02,
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      theme: mode === "dark" ? "dark" : "light",
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        if (dataPointIndex < 0 || dataPointIndex >= categories.length) {
          return "";
        }
        const locationName =
          categories[dataPointIndex] ||
          w?.globals?.categoryLabels?.[dataPointIndex] ||
          "";
        const count =
          series?.[seriesIndex]?.[dataPointIndex] ??
          dataList[dataPointIndex] ??
          0;
        const isDark = mode === "dark";
        const bg = isDark ? "#1e1e2d" : "#ffffff";
        const textCol = isDark ? "#ffffff" : "#0f172a";
        const borderCol = isDark ? "#2b2b40" : "#e2e8f0";

        return (
          '<div style="padding: 10px 14px; font-family: inherit; font-size: 12px; background: ' +
          bg +
          "; border-radius: 8px; box-shadow: 0 8px 22px rgba(0,0,0,0.14); border: 1px solid " +
          borderCol +
          '; max-width: 340px; pointer-events: none;">' +
          '<div style="font-weight: 700; color: ' +
          textCol +
          '; margin-bottom: 6px; word-break: break-word; line-height: 1.45; font-size: 13px;">' +
          "📍 " +
          escapeHtml(locationName) +
          "</div>" +
          '<div style="color: #0284c7; font-weight: 700; font-size: 13px;">' +
          count +
          " Group(s)" +
          "</div>" +
          "</div>"
        );
      },
    },
    colors: ["#0284C7"],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 5,
      yaxis: {
        lines: {
          show: true,
        },
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
      padding: {
        top: 18,
        right: 12,
        bottom: 0,
        left: 12,
      },
    },
  };
}

function escapeHtml(str: string): string {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
